'use client'

import {
    BalanceteAccount,
    BalanceteReport,
    exportBalancete,
    getBalanceteReport,
} from '@/lib/api/financial/financial-accounting-api'
import { useEffect, useMemo, useState } from 'react'
import {
    MdArrowBack,
    MdChevronRight,
    MdFileDownload,
    MdPrint,
    MdSearch,
    MdWarning,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

const formatCurrency = (cents: number) =>
    (Math.abs(cents) / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })

/** Data local em ISO, sem passar pelo UTC do toISOString(). */
const toISODate = (date: Date) => {
    const offset = date.getTimezoneOffset() * 60000
    return new Date(date.getTime() - offset).toISOString().slice(0, 10)
}

/** '2026-08-01' -> '01/08/2026', sem deslocar o dia pelo fuso. */
const formatDateBR = (iso: string) => {
    const [year, month, day] = iso.slice(0, 10).split('-')
    return `${day}/${month}/${year}`
}

const getDefaultDates = () => {
    const now = new Date()
    return {
        startDate: toISODate(new Date(now.getFullYear(), now.getMonth(), 1)),
        endDate: toISODate(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
    }
}

const PERIOD_SHORTCUTS: { label: string; range: () => { startDate: string; endDate: string } }[] = [
    { label: 'Mês atual', range: () => getDefaultDates() },
    {
        label: 'Mês anterior',
        range: () => {
            const now = new Date()
            return {
                startDate: toISODate(new Date(now.getFullYear(), now.getMonth() - 1, 1)),
                endDate: toISODate(new Date(now.getFullYear(), now.getMonth(), 0)),
            }
        },
    },
    {
        label: 'Trimestre',
        range: () => {
            const now = new Date()
            const firstMonth = Math.floor(now.getMonth() / 3) * 3
            return {
                startDate: toISODate(new Date(now.getFullYear(), firstMonth, 1)),
                endDate: toISODate(new Date(now.getFullYear(), firstMonth + 3, 0)),
            }
        },
    },
    {
        label: 'Ano',
        range: () => {
            const now = new Date()
            return {
                startDate: toISODate(new Date(now.getFullYear(), 0, 1)),
                endDate: toISODate(new Date(now.getFullYear(), 11, 31)),
            }
        },
    },
]

/**
 * Antes só existiam dois rótulos — "Entrada" para INCOME e "Saída" para todo o
 * resto —, então Caixa, Bancos e Capital Social apareciam como "Saída".
 */
const ACCOUNT_TYPES: Record<string, { label: string; badge: string }> = {
    ASSET: { label: 'Ativo', badge: 'badge-info' },
    LIABILITY: { label: 'Passivo', badge: 'badge-warning' },
    EQUITY: { label: 'Patrimônio', badge: 'badge-neutral' },
    INCOME: { label: 'Receita', badge: 'badge-success' },
    EXPENSE: { label: 'Despesa', badge: 'badge-error' },
}

/** No balancete o saldo é devedor (D) ou credor (C) — não é bom nem ruim. */
const SaldoCell = ({ value }: { value: number }) => {
    if (value === 0) return <span className="text-base-content/30">—</span>
    return (
        <span className="font-mono">
            {formatCurrency(value)}
            <span className="ml-1 text-xs text-base-content/50">
                {value > 0 ? 'D' : 'C'}
            </span>
        </span>
    )
}

function TypeTotals({
    totals,
}: {
    totals: Record<string, { debit: number; credit: number; finalBalance: number }>
}) {
    const entries = Object.entries(totals).filter(
        ([, value]) => value.debit !== 0 || value.credit !== 0 || value.finalBalance !== 0,
    )
    if (entries.length === 0) return null

    return (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
            {entries.map(([type, value]) => (
                <div
                    key={type}
                    className="rounded-lg border border-base-300/60 bg-base-100 p-3"
                >
                    <p className="text-xs font-medium uppercase tracking-wide text-base-content/50">
                        {ACCOUNT_TYPES[type]?.label || type}
                    </p>
                    <p className="mt-1 font-mono text-sm font-bold">
                        {formatCurrency(value.finalBalance)}
                        <span className="ml-1 text-xs font-normal text-base-content/50">
                            {value.finalBalance >= 0 ? 'D' : 'C'}
                        </span>
                    </p>
                </div>
            ))}
        </div>
    )
}

export default function BalanceteReportPage() {
    const navigate = useNavigate()
    const [data, setData] = useState<BalanceteReport | null>(null)
    const [loading, setLoading] = useState(true)
    const [startDate, setStartDate] = useState(getDefaultDates().startDate)
    const [endDate, setEndDate] = useState(getDefaultDates().endDate)
    const [search, setSearch] = useState('')
    const [hideEmpty, setHideEmpty] = useState(false)

    useEffect(() => {
        loadData()
    }, [startDate, endDate])

    const loadData = async () => {
        setLoading(true)
        try {
            const res = await getBalanceteReport(startDate, endDate)
            if (res.success && res.result) setData(res.result)
        } catch {
            toast.error('Erro ao carregar balancete')
        } finally {
            setLoading(false)
        }
    }

    const accounts: BalanceteAccount[] = useMemo(() => {
        if (!data) return []
        const term = search.trim().toLowerCase()
        return data.accounts.filter(account => {
            if (hideEmpty && account.debit === 0 && account.credit === 0) return false
            if (!term) return true
            return (
                account.code.toLowerCase().includes(term) ||
                account.title.toLowerCase().includes(term)
            )
        })
    }, [data, search, hideEmpty])

    const difference = data?.difference ?? (data ? data.totalDebit - data.totalCredit : 0)
    const activeShortcut = PERIOD_SHORTCUTS.find(shortcut => {
        const range = shortcut.range()
        return range.startDate === startDate && range.endDate === endDate
    })

    return (
        <div className="mx-auto max-w-6xl p-4 md:p-6">
            <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)}>
                        <MdArrowBack size={20} />
                    </button>
                    <h1 className="text-2xl font-bold">Balancete de Verificação</h1>
                </div>
                <div className="flex items-center gap-1 print:hidden">
                    <div className="dropdown dropdown-end">
                        <label tabIndex={0} className="btn btn-sm btn-outline gap-1">
                            <MdFileDownload size={16} />
                            Exportar
                        </label>
                        <ul
                            tabIndex={0}
                            className="dropdown-content menu z-50 w-44 rounded-box border border-base-300 bg-base-100 p-2 shadow-lg"
                        >
                            <li>
                                <button
                                    onClick={() => {
                                        exportBalancete(startDate, endDate, 'xlsx').catch(() =>
                                            toast.error('Erro ao exportar'),
                                        )
                                    }}
                                >
                                    Excel (.xlsx)
                                </button>
                            </li>
                            <li>
                                <button
                                    onClick={() => {
                                        exportBalancete(startDate, endDate, 'pdf').catch(() =>
                                            toast.error('Erro ao exportar'),
                                        )
                                    }}
                                >
                                    PDF
                                </button>
                            </li>
                        </ul>
                    </div>
                    <button className="btn btn-ghost btn-sm" onClick={() => window.print()}>
                        <MdPrint size={18} />
                    </button>
                </div>
            </div>

            {/* Atalhos de período */}
            <div className="mb-3 flex flex-wrap gap-2 print:hidden">
                {PERIOD_SHORTCUTS.map(shortcut => (
                    <button
                        key={shortcut.label}
                        className={`btn btn-xs ${activeShortcut?.label === shortcut.label ? 'btn-secondary' : 'btn-ghost border border-base-300'}`}
                        onClick={() => {
                            const range = shortcut.range()
                            setStartDate(range.startDate)
                            setEndDate(range.endDate)
                        }}
                    >
                        {shortcut.label}
                    </button>
                ))}
            </div>

            <div className="mb-6 flex flex-wrap items-end gap-3 print:hidden">
                <div className="form-control">
                    <label className="label">
                        <span className="label-text text-xs">Início</span>
                    </label>
                    <input
                        type="date"
                        className="input input-bordered input-sm"
                        value={startDate}
                        onChange={e => setStartDate(e.target.value)}
                    />
                </div>
                <div className="form-control">
                    <label className="label">
                        <span className="label-text text-xs">Fim</span>
                    </label>
                    <input
                        type="date"
                        className="input input-bordered input-sm"
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                    />
                </div>
                <div className="form-control">
                    <label className="label">
                        <span className="label-text text-xs">Buscar conta</span>
                    </label>
                    <label className="input input-bordered input-sm flex items-center gap-2">
                        <MdSearch size={16} className="text-base-content/40" />
                        <input
                            type="text"
                            className="grow"
                            placeholder="Código ou nome"
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                    </label>
                </div>
                <label className="label cursor-pointer gap-2 pb-2">
                    <input
                        type="checkbox"
                        className="checkbox checkbox-sm"
                        checked={hideEmpty}
                        onChange={e => setHideEmpty(e.target.checked)}
                    />
                    <span className="label-text text-xs">Só contas com movimento no período</span>
                </label>
            </div>

            {loading && (
                <div className="flex justify-center py-20">
                    <div className="loading loading-spinner loading-lg text-secondary"></div>
                </div>
            )}

            {!loading && data && (
                <div className="space-y-6">
                    {/* Verificação das partidas dobradas */}
                    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                        <div className="rounded-xl border border-base-300/60 bg-base-100 p-4">
                            <p className="text-xs font-medium uppercase tracking-wide text-base-content/50">
                                Total débitos
                            </p>
                            <p className="mt-1 font-mono text-xl font-bold">
                                {formatCurrency(data.totalDebit)}
                            </p>
                        </div>
                        <div className="rounded-xl border border-base-300/60 bg-base-100 p-4">
                            <p className="text-xs font-medium uppercase tracking-wide text-base-content/50">
                                Total créditos
                            </p>
                            <p className="mt-1 font-mono text-xl font-bold">
                                {formatCurrency(data.totalCredit)}
                            </p>
                        </div>
                        <div
                            className={`rounded-xl border p-4 ${difference === 0 ? 'border-base-300/60 bg-base-100' : 'border-error bg-error/5'}`}
                        >
                            <p className="text-xs font-medium uppercase tracking-wide text-base-content/50">
                                Diferença
                            </p>
                            <p
                                className={`mt-1 flex items-center gap-1 font-mono text-xl font-bold ${difference === 0 ? 'text-success' : 'text-error'}`}
                            >
                                {difference !== 0 && <MdWarning size={18} />}
                                {formatCurrency(difference)}
                            </p>
                            <p className="mt-0.5 text-xs text-base-content/60">
                                {difference === 0
                                    ? 'Partidas conferem'
                                    : 'Há lançamento desbalanceado'}
                            </p>
                        </div>
                        <div className="rounded-xl border border-base-300/60 bg-base-100 p-4">
                            <p className="text-xs font-medium uppercase tracking-wide text-base-content/50">
                                Contas
                            </p>
                            <p className="mt-1 font-mono text-xl font-bold">
                                {accounts.length}
                                {accounts.length !== data.accounts.length && (
                                    <span className="ml-1 text-sm font-normal text-base-content/50">
                                        de {data.accounts.length}
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>

                    {data.totalsByType && <TypeTotals totals={data.totalsByType} />}

                    <div className="card overflow-x-auto bg-base-100 shadow print:shadow-none">
                        <div className="card-body p-6">
                            <div className="mb-6 text-center">
                                <h2 className="text-lg font-bold">Balancete de Verificação</h2>
                                <p className="text-sm text-base-content/60">
                                    {formatDateBR(startDate)} a {formatDateBR(endDate)}
                                </p>
                            </div>

                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Código</th>
                                        <th>Conta</th>
                                        <th>Tipo</th>
                                        <th className="text-right">Saldo anterior</th>
                                        <th className="text-right">Débito</th>
                                        <th className="text-right">Crédito</th>
                                        <th className="text-right">Saldo atual</th>
                                        <th className="w-6 print:hidden"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {accounts.length === 0 && (
                                        <tr>
                                            <td
                                                colSpan={8}
                                                className="py-8 text-center text-base-content/40"
                                            >
                                                {data.accounts.length === 0
                                                    ? 'Nenhuma movimentação no período'
                                                    : 'Nenhuma conta corresponde ao filtro'}
                                            </td>
                                        </tr>
                                    )}
                                    {accounts.map(account => {
                                        const type = ACCOUNT_TYPES[account.type]
                                        return (
                                            <tr
                                                key={account.accountId}
                                                className="hover cursor-pointer"
                                                onClick={() =>
                                                    navigate(
                                                        `/financial/relatorios/razao/${account.accountId}`,
                                                    )
                                                }
                                                title="Ver razão da conta"
                                            >
                                                <td className="font-mono font-semibold text-secondary">
                                                    {account.code}
                                                </td>
                                                <td>{account.title}</td>
                                                <td>
                                                    <span
                                                        className={`badge badge-xs ${type?.badge || 'badge-ghost'}`}
                                                    >
                                                        {type?.label || account.type}
                                                    </span>
                                                </td>
                                                <td className="text-right">
                                                    <SaldoCell value={account.previousBalance ?? 0} />
                                                </td>
                                                <td className="text-right font-mono">
                                                    {account.debit > 0
                                                        ? formatCurrency(account.debit)
                                                        : '—'}
                                                </td>
                                                <td className="text-right font-mono">
                                                    {account.credit > 0
                                                        ? formatCurrency(account.credit)
                                                        : '—'}
                                                </td>
                                                <td className="text-right font-semibold">
                                                    <SaldoCell
                                                        value={
                                                            account.finalBalance ?? account.balance
                                                        }
                                                    />
                                                </td>
                                                <td className="print:hidden">
                                                    <MdChevronRight
                                                        size={16}
                                                        className="text-base-content/30"
                                                    />
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>
                                <tfoot>
                                    <tr className="border-t-2 font-bold">
                                        <td colSpan={4}>TOTAL</td>
                                        <td className="text-right font-mono">
                                            {formatCurrency(data.totalDebit)}
                                        </td>
                                        <td className="text-right font-mono">
                                            {formatCurrency(data.totalCredit)}
                                        </td>
                                        <td
                                            className={`text-right font-mono ${difference === 0 ? '' : 'text-error'}`}
                                        >
                                            {difference === 0
                                                ? 'Confere'
                                                : formatCurrency(difference)}
                                        </td>
                                        <td className="print:hidden"></td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
